import React, { useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Download, FileText } from "lucide-react";
import { toast } from "sonner";

export function MaterialPreview({ materialId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [material, setMaterial] = useState(null);
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!open || !materialId) return;
    setLoading(true); setUrl(null); setMaterial(null);
    (async () => {
      try {
        const m = (await api.get(`/materials/${materialId}`)).data;
        setMaterial(m);
        const res = (await api.get(`/materials/${materialId}/preview`)).data;
        setUrl(`${BACKEND_URL}${res.url}`);
      } catch {
        toast.error("Could not load preview");
      } finally { setLoading(false); }
    })();
  }, [open, materialId]);

  const download = async () => {
    try {
      const res = (await api.get(`/materials/${materialId}/download`)).data;
      window.open(`${BACKEND_URL}${res.url}`, "_blank");
    } catch { toast.error("Download failed"); }
  };

  const ext = material?.file_extension?.toLowerCase();
  const isImage = ["png", "jpg", "jpeg", "webp", "gif"].includes(ext);
  const isPdf = ext === "pdf";
  const isVideo = ["mp4", "mov", "webm"].includes(ext);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="material-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 pr-8">
            <FileText size={18} /> {material?.title || "Preview"}
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-[300px] flex items-center justify-center bg-muted/40 rounded-lg overflow-hidden">
          {loading ? (
            <Loader2 className="animate-spin text-muted-foreground" />
          ) : isImage && url ? (
            <img src={url} alt={material?.title} className="max-h-[65vh] w-auto" data-testid="preview-image" />
          ) : isPdf && url ? (
            <iframe src={url} title="pdf" className="w-full h-[65vh]" data-testid="preview-pdf" />
          ) : isVideo && url ? (
            <video src={url} controls className="max-h-[65vh] w-full" data-testid="preview-video" />
          ) : (
            <div className="flex flex-col items-center gap-3 p-10 text-center text-muted-foreground">
              <FileText size={40} />
              <p>{material?.file_name}</p>
              <p className="text-xs uppercase">{ext} · preview not available in-app</p>
            </div>
          )}
        </div>
        {material?.description && <p className="text-sm text-muted-foreground">{material.description}</p>}
        <div className="flex justify-end">
          <Button onClick={download} className="gap-2" data-testid="preview-download-button">
            <Download size={16} /> Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
